import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useMemo } from "react";
import { notEmpty } from "@orb-labs/types";
import { PlatformStorageContext } from "../storage/PlatformStorageContext.js";
import { OrbyProvider } from "../components/OrbyProvider/OrbyProvider.js";
import { useAuthentication } from "../providers/auth/AuthenticationProvider.js";
import { BullrunRuntimeProvider } from "../runtime/BullrunRuntimeContext.js";
import { PlatformObservabilityProvider } from "../observability/PlatformObservabilityContext.js";
import { PlatformLifecycleProvider } from "../lifecycle/PlatformLifecycleContext.js";
import { AppSettingsProvider } from "../settings/AppSettingsContext.js";
import { ObservabilityProvider } from "../observability/ObservabilityProvider.js";
import { GraphQLProvider } from "./GraphQLProvider.js";
import { LiveSubscriptionInterestsProvider } from "./LiveSubscriptionInterestsProvider.js";
import { LiveUpdatesProvider } from "./LiveUpdatesProvider.js";
import { HyperliquidProvider } from "./HyperliquidProvider.js";
import { PolymarketProvider } from "./PolymarketProvider.js";
/**
 * Bundles authentication wiring, OrbyProvider, GraphQL, live-update,
 * observability, Polymarket, and Hyperliquid providers. Mount below
 * PlatformGraphQLProvider, PlatformStorageProvider, and AuthenticationProvider.
 *
 * Calls `useAuthentication()` internally to build the OrbyProvider config and
 * BullrunRuntime (signTypedData). Pass static app config (appName, keys, URL)
 * and platform-specific DI values (observability, lifecycle, analytics).
 *
 * MeshProvider is NOT included here because AuthenticationProvider consumes
 * `useMesh()` to derive accounts that feed OrbyProvider — so MeshProvider
 * must live ABOVE this component in the tree.
 */
export const BullrunProviders = ({ appName, instancePrivateAPIKey, instancePublicAPIKey, baseUrl, observability, lifecycle, analytics, errorMonitoring, loadingFallback, children, }) => {
    const storage = useContext(PlatformStorageContext);
    if (!notEmpty(storage)) {
        throw new Error("BullrunProviders must be mounted below PlatformStorageProvider");
    }
    const { isLoading, accounts, authentication, signTypedData } = useAuthentication();
    const orbyConfig = useMemo(() => ({
        appName,
        instancePrivateAPIKey,
        instancePublicAPIKey,
        baseUrl,
        accounts: authentication ? accounts ?? [] : [],
        authentication,
    }), [appName, instancePrivateAPIKey, instancePublicAPIKey, baseUrl, accounts, authentication]);
    const runtime = useMemo(() => ({ signTypedData }), [signTypedData]);
    if (loadingFallback && (isLoading || !accounts)) {
        return loadingFallback;
    }
    return (_jsx(OrbyProvider, { config: orbyConfig, children: _jsx(PlatformObservabilityProvider, { value: observability, children: _jsx(BullrunRuntimeProvider, { value: runtime, children: _jsx(PlatformLifecycleProvider, { value: lifecycle, children: _jsx(AppSettingsProvider, { load: storage.loadAppSettings, save: storage.saveAppSettings, children: _jsx(GraphQLProvider, { children: _jsx(LiveSubscriptionInterestsProvider, { children: _jsx(LiveUpdatesProvider, { children: _jsx(ObservabilityProvider, { analytics: analytics, errorMonitoring: errorMonitoring, children: _jsx(PolymarketProvider, { children: _jsx(HyperliquidProvider, { children: children }) }) }) }) }) }) }) }) }) }) }));
};
